import { weekDayDateKeyFromMonday } from './strategySheetMetrics';
import { resolveConfiguredDisplaySymbol } from './strategyInstrument';

export const DEFAULT_VECTOR_CAPITAL_BASE_VALUE = 100000;

/**
 * Parse a series point's date key to a UTC Date. Keys are normally 'YYYY-MM-DD'
 * (snapped to midday UTC to dodge timezone edges), but live test strategies use
 * intraday keys like 'YYYY-MM-DDTHH:MM'. Returns a valid Date for either, never
 * an Invalid Date (falls back to the date portion at midday).
 */
export function parseSeriesDateKey(key) {
  const s = String(key || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T12:00:00Z`);
  let iso = s.replace(' ', 'T');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) iso = `${iso}:00`;
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) iso = `${iso}Z`;
  const dt = new Date(iso);
  if (!Number.isNaN(dt.getTime())) return dt;
  return new Date(`${s.slice(0, 10)}T12:00:00Z`);
}

export function parseDateFromRowLabel(label) {
  if (!label) return null;
  const dateMatch = String(label).match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (!dateMatch) return null;
  const [, mm, dd, yyyy] = dateMatch;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  const year = parseInt(yyyy, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1901) return null;
  return new Date(year, month - 1, day);
}

export function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  let startDate = null;
  let endDate = null;

  switch (filter) {
    case 'all-time':
      return { startDate: null, endDate: null };
    case 'last-30-days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      endDate = now;
      break;
    case 'last-3-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      endDate = now;
      break;
    case 'last-6-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      endDate = now;
      break;
    case 'last-12-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 12);
      endDate = now;
      break;
    case 'ytd':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = now;
      break;
    case '2024':
      startDate = new Date(2024, 0, 1);
      endDate = new Date(2024, 11, 31);
      break;
    case '2025':
      startDate = new Date(2025, 0, 1);
      endDate = new Date(2025, 11, 31);
      break;
    case 'custom':
      if (customStart) startDate = new Date(customStart);
      if (customEnd) endDate = new Date(customEnd);
      break;
    default:
      return { startDate: null, endDate: null };
  }

  return { startDate, endDate };
}

/** ISO date bounds for series rows (inclusive). Null bounds = no filter on that side. */
export function filterSeriesByDateRange(series, startIso, endIso) {
  const rows = Array.isArray(series) ? series : [];
  return rows.filter((p) => {
    if (!p?.date) return false;
    if (startIso && p.date < startIso) return false;
    if (endIso && p.date > endIso) return false;
    return true;
  });
}

/**
 * Extend a series to the selected filter window with flat anchors at the range
 * edges (Fyntrak parity: metrics annualize over the full filter span, not just
 * first/last trade dates inside the window).
 */
export function padSeriesToDateRange(series, startIso, endIso, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  if (!Array.isArray(series) || series.length === 0) return series || [];
  let out = [...series];
  const first = out[0];
  const last = out[out.length - 1];
  if (startIso && first.date > startIso) {
    out.unshift({
      date: startIso,
      normalized: baseValue,
      dailyPnl: 0,
      cumulative: 0,
      dailyReturnPct: 0,
    });
  }
  if (endIso && last.date < endIso) {
    const tailNorm = out[out.length - 1].normalized;
    out.push({
      date: endIso,
      normalized: tailNorm,
      dailyPnl: 0,
      cumulative: Math.round((tailNorm - baseValue) * 100) / 100,
      dailyReturnPct: 0,
    });
  }
  return out;
}

/**
 * Rebase a normalized value series so the first point equals baseValue (period
 * start = 0% return). Required when a series was normalized to an earlier
 * anchor (e.g. benchmark history from 2022) but metrics should reflect the
 * active date-range filter only.
 */
export function rebaseSeriesToPeriodStart(series, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const startNorm = Number(series[0]?.normalized);
  if (!Number.isFinite(startNorm) || startNorm <= 0) return series;
  if (Math.abs(startNorm - baseValue) < 0.005) return series;
  const factor = baseValue / startNorm;
  let prevNorm = baseValue;
  return series.map((p, i) => {
    const normalized = Math.round(Number(p.normalized) * factor * 100) / 100;
    const dailyPnl = i === 0 ? 0 : Math.round((normalized - prevNorm) * 100) / 100;
    const dailyReturnPct = i === 0 || prevNorm <= 0
      ? 0
      : Math.round(((normalized - prevNorm) / prevNorm) * 10000) / 10000;
    prevNorm = normalized;
    return {
      ...p,
      normalized,
      dailyPnl,
      cumulative: Math.round((normalized - baseValue) * 100) / 100,
      dailyReturnPct,
    };
  });
}

/**
 * Fyntrak getPerformanceData parity for a single equity-curve leg.
 *
 * Trade-journal curves are stored on a shared fixed notional (`pointBase`, usually
 * $100k). Fyntrak portfolio legs use per-ticker `equityPerTrade` (AAPL $100k, others
 * $10k) when converting cum_pnl → cumulative %:
 *   value = ((cumPnl + legCapital) / (baseCumPnl + legCapital) - 1) × 100
 * where cumPnl = equity − pointBase.
 *
 * @param {Array<{date:string,equity:number}>} points
 * @param {{startDate?:string|null,endDate?:string|null}} range
 * @param {number} [legCapital] Fyntrak equityPerTrade (defaults to pointBase)
 */
export function buildLegRebasedPerformanceData(points, range = {}) {
  const rows = (Array.isArray(points) ? points : [])
    .filter((p) => p && typeof p.date === 'string' && Number.isFinite(Number(p.equity)))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!rows.length) return [];

  const startDate = range.startDate || null;
  const endDate = range.endDate || null;
  const pointBase = Number(rows[0].equity);
  if (!Number.isFinite(pointBase) || pointBase <= 0) return [];

  // Rebase on the curve's OWN stored base. Every RiskLo equity curve is stored
  // on the same base (e.g. $100k), so each leg's % return is measured against
  // that base — matching Fyntrak (value = cum_pnl / storage_base). Dividing by a
  // per-leg notional (equityPerTrade) that differs from the stored base inflated
  // every leg whose equityPerTrade was $10k by 10×.
  const capital = pointBase;

  const equityToCumPnl = (equity) => Number(equity) - pointBase;

  let baseCumPnl = 0;
  let prevRow = null;
  if (startDate) {
    for (let i = 0; i < rows.length; i += 1) {
      if (rows[i].date < startDate) {
        prevRow = rows[i];
        baseCumPnl = equityToCumPnl(rows[i].equity);
      } else break;
    }
  }

  const baseEquity = baseCumPnl + capital;
  if (!Number.isFinite(baseEquity) || baseEquity <= 0) return [];

  const result = [];
  if (prevRow) result.push({ date: prevRow.date, value: 0 });

  rows.forEach((p) => {
    if (startDate && p.date < startDate) return;
    if (endDate && p.date > endDate) return;
    const cumPnl = equityToCumPnl(p.equity);
    const currentEquity = cumPnl + capital;
    result.push({
      date: p.date,
      value: ((currentEquity / baseEquity) - 1) * 100,
    });
  });
  return result;
}

/**
 * Fyntrak getEquityPortfolioPerformance parity: weighted sum of rebased leg
 * performance curves, converted to a normalized account-value series.
 */
export function buildEquityPortfolioPerformanceSeries(
  legs,
  leverage,
  strategyByName,
  range = {},
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
) {
  const lev = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  const legEntries = [];
  (Array.isArray(legs) ? legs : []).forEach((leg) => {
    if (!leg?.found || !leg.strategyName) return;
    const strat = strategyByName?.get?.(leg.strategyName);
    const points = Array.isArray(strat?.points) ? strat.points : [];
    if (!points.length) return;
    const data = buildLegRebasedPerformanceData(points, range);
    if (!data.length) return;
    const weight = lev * ((Number(leg.weightPct) || 0) / 100);
    if (weight === 0) return;
    legEntries.push({ name: leg.strategyName, weight, data });
  });
  if (!legEntries.length) return [];

  const allDates = Array.from(
    new Set(legEntries.flatMap((e) => e.data.map((p) => p.date))),
  ).sort((a, b) => a.localeCompare(b));
  const maps = new Map(legEntries.map((e) => [e.name, new Map(e.data.map((p) => [p.date, p.value]))]));
  const lastKnown = new Map();
  let prevNormalized = baseValue;

  return allDates.map((date) => {
    let portfolioValue = 0;
    legEntries.forEach(({ name, weight }) => {
      const m = maps.get(name);
      if (m.has(date)) lastKnown.set(name, m.get(date));
      const v = lastKnown.get(name);
      if (v !== undefined) portfolioValue += weight * v;
    });
    const normalized = baseValue * (1 + portfolioValue / 100);
    const dailyPnl = normalized - prevNormalized;
    const dailyReturnPct = prevNormalized > 0 ? (dailyPnl / prevNormalized) * 100 : 0;
    prevNormalized = normalized;
    return {
      date,
      dailyPnl: Math.round(dailyPnl * 100) / 100,
      cumulative: Math.round((normalized - baseValue) * 100) / 100,
      normalized: Math.round(normalized * 100) / 100,
      dailyReturnPct: Math.round(dailyReturnPct * 10000) / 10000,
    };
  });
}

export function filterRowsByDateRange(rows, startDate, endDate) {
  if (!startDate && !endDate) return rows;
  if (!rows || rows.length <= 2) return rows || [];
  const headers = rows.slice(0, 2);
  const dataRows = rows.slice(2);
  const filteredDataRows = dataRows.filter((row) => {
    const dateLabel = Array.isArray(row) ? row[0] : (row?.date || row?.label || row?.week);
    const rowDate = parseDateFromRowLabel(dateLabel);
    if (!rowDate) return true;
    if (startDate && rowDate < startDate) return false;
    if (endDate && rowDate > endDate) return false;
    return true;
  });
  return [...headers, ...filteredDataRows];
}

export function parseNumericCell(value) {
  if (value === undefined || value === null || value === '') return null;
  const cleaned = String(value).replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

export function combinePortfolioRawRows(strategyNames, rawDataMap) {
  const names = [...strategyNames];
  if (names.length === 0) return [];
  let template = null;
  for (const name of names) {
    const rows = rawDataMap[name];
    if (rows && rows.length >= 3) {
      template = rows;
      break;
    }
  }
  if (!template) return [];

  const header0 = Array.isArray(template[0]) ? [...template[0]] : [];
  const header1 = Array.isArray(template[1]) ? [...template[1]] : [];
  const idxMonday = header1.findIndex((cell) => String(cell || '').toLowerCase() === 'monday');
  const idxTuesday = header1.findIndex((cell) => String(cell || '').toLowerCase() === 'tuesday');
  const idxWednesday = header1.findIndex((cell) => String(cell || '').toLowerCase() === 'wednesday');
  const idxThursday = header1.findIndex((cell) => String(cell || '').toLowerCase() === 'thursday');
  const idxFriday = header1.findIndex((cell) => String(cell || '').toLowerCase() === 'friday');
  const mondayCol = idxMonday >= 0 ? idxMonday : 2;
  const tuesdayCol = idxTuesday >= 0 ? idxTuesday : 3;
  const wednesdayCol = idxWednesday >= 0 ? idxWednesday : 4;
  const thursdayCol = idxThursday >= 0 ? idxThursday : 5;
  const fridayCol = idxFriday >= 0 ? idxFriday : 6;
  const dayCols = [mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol];
  const rowLen = Math.max(header1.length, fridayCol + 1, 8);

  const byMonday = new Map();
  names.forEach((name) => {
    const rows = rawDataMap[name];
    if (!rows || rows.length < 3) return;
    rows.slice(2).forEach((row) => {
      const mondayKey = row[0];
      if (!mondayKey) return;
      const match = String(mondayKey).match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (!match) return;
      let acc = byMonday.get(mondayKey);
      if (!acc) {
        acc = new Array(rowLen).fill('');
        acc[0] = mondayKey;
        for (let i = 1; i < rowLen; i += 1) acc[i] = 0;
        byMonday.set(mondayKey, acc);
      }
      dayCols.forEach((col) => {
        const value = parseNumericCell(row[col]);
        acc[col] = (Number(acc[col]) || 0) + (value || 0);
      });
    });
  });

  const sortedKeys = Array.from(byMonday.keys()).sort((a, b) => {
    const da = parseDateFromRowLabel(a);
    const db = parseDateFromRowLabel(b);
    if (!da || !db) return 0;
    return da - db;
  });

  return [header0, header1, ...sortedKeys.map((key) => byMonday.get(key))];
}

/** Virtual Vector Results entry: weekday-specific merge (see combineMultiStratRawRows).
 *  Renamed from "Multi-Strat" → "Vector Master Strat" on 2026-05-21. The
 *  former name still works via isLegacyMultiStratName below so any saved
 *  widget configs / dashboard templates / API params with the old value
 *  resolve to the same virtual sheet. */
export const MULTI_STRAT_SHEET_NAME = 'Vector Master Strat';
export const LEGACY_MULTI_STRAT_NAMES = ['Multi-Strat'];

export function isMultiStratName(name) {
  if (typeof name !== 'string') return false;
  if (name === MULTI_STRAT_SHEET_NAME) return true;
  return LEGACY_MULTI_STRAT_NAMES.includes(name);
}

export const MULTI_STRAT_SOURCE_SHEETS = {
  monWedFri: 'Grass Fed Prime Beef (Current version 2.1C)',
  tuesday: 'Tuesday Special (Previous 2.2A)',
  thursday: 'Thursday Special',
};

function rowsByMondayKeyForMultiStrat(rows) {
  const map = new Map();
  if (!rows || rows.length < 3) return map;
  rows.slice(2).forEach((row) => {
    const key = row[0];
    if (!key) return;
    if (!String(key).match(/\d{1,2}\.\d{1,2}\.\d{4}/)) return;
    map.set(key, row);
  });
  return map;
}

function dayColumnIndicesFromHeader(headerRow) {
  const h = headerRow || [];
  const idxMonday = h.findIndex((cell) => String(cell || '').toLowerCase() === 'monday');
  const idxTuesday = h.findIndex((cell) => String(cell || '').toLowerCase() === 'tuesday');
  const idxWednesday = h.findIndex((cell) => String(cell || '').toLowerCase() === 'wednesday');
  const idxThursday = h.findIndex((cell) => String(cell || '').toLowerCase() === 'thursday');
  const idxFriday = h.findIndex((cell) => String(cell || '').toLowerCase() === 'friday');
  return {
    monday: idxMonday >= 0 ? idxMonday : 2,
    tuesday: idxTuesday >= 0 ? idxTuesday : 3,
    wednesday: idxWednesday >= 0 ? idxWednesday : 4,
    thursday: idxThursday >= 0 ? idxThursday : 5,
    friday: idxFriday >= 0 ? idxFriday : 6,
  };
}

function cellAtCol(row, colIdx) {
  if (!row || colIdx < 0) return '';
  const v = row[colIdx];
  if (v === undefined || v === null) return '';
  return v;
}

/**
 * Mon/Wed/Fri from Grass Fed Prime Beef (Current version 2.1C), Tue from Tuesday Special (Previous 2.2A), Thu from Thursday Special.
 */
export function combineMultiStratRawRows(rowsMonWedFri, rowsTuesday, rowsThursday) {
  const template =
    (rowsMonWedFri?.length >= 3 && rowsMonWedFri)
    || (rowsTuesday?.length >= 3 && rowsTuesday)
    || rowsThursday;
  if (!template || template.length < 3) return [];

  const header0 = Array.isArray(template[0]) ? [...template[0]] : [];
  const header1 = Array.isArray(template[1]) ? [...template[1]] : [];
  const day = dayColumnIndicesFromHeader(header1);
  const rowLen = Math.max(
    header1.length,
    template[2]?.length || 0,
    template[3]?.length || 0,
    day.friday + 1,
    8,
  );

  const map21 = rowsByMondayKeyForMultiStrat(rowsMonWedFri);
  const mapTuesday = rowsByMondayKeyForMultiStrat(rowsTuesday);
  const mapThu = rowsByMondayKeyForMultiStrat(rowsThursday);

  const allKeys = new Set([...map21.keys(), ...mapTuesday.keys(), ...mapThu.keys()]);
  const sortedKeys = Array.from(allKeys).sort((a, b) => {
    const da = parseDateFromRowLabel(a);
    const db = parseDateFromRowLabel(b);
    if (!da || !db) return 0;
    return da - db;
  });

  const dataRows = sortedKeys.map((mondayKey) => {
    const out = new Array(rowLen).fill('');
    out[0] = mondayKey;
    out[day.monday] = cellAtCol(map21.get(mondayKey), day.monday);
    out[day.tuesday] = cellAtCol(mapTuesday.get(mondayKey), day.tuesday);
    out[day.wednesday] = cellAtCol(map21.get(mondayKey), day.wednesday);
    out[day.thursday] = cellAtCol(mapThu.get(mondayKey), day.thursday);
    out[day.friday] = cellAtCol(map21.get(mondayKey), day.friday);
    return out;
  });

  return [header0, header1, ...dataRows];
}

export function combineScenarioSleeveRows(sleeves, rawDataMap) {
  const activeSleeves = Array.isArray(sleeves)
    ? sleeves.filter((sleeve) => sleeve?.strategyName && rawDataMap?.[sleeve.strategyName]?.length >= 3)
    : [];
  if (!activeSleeves.length) return [];

  let template = null;
  activeSleeves.forEach((sleeve) => {
    if (!template && rawDataMap[sleeve.strategyName]?.length >= 3) {
      template = rawDataMap[sleeve.strategyName];
    }
  });
  if (!template) return [];

  const header0 = Array.isArray(template[0]) ? [...template[0]] : [];
  const header1 = Array.isArray(template[1]) ? [...template[1]] : [];
  const day = dayColumnIndicesFromHeader(header1);
  const dayEntries = [
    ['monday', day.monday],
    ['tuesday', day.tuesday],
    ['wednesday', day.wednesday],
    ['thursday', day.thursday],
    ['friday', day.friday],
  ];
  const rowLen = Math.max(header1.length, day.friday + 1, 8);
  const byMonday = new Map();

  activeSleeves.forEach((sleeve) => {
    const rows = rawDataMap[sleeve.strategyName];
    if (!rows || rows.length < 3) return;
    const allowedDays = new Set(
      Array.isArray(sleeve.activeDays)
        ? sleeve.activeDays.map((entry) => String(entry || '').trim().toLowerCase())
        : [],
    );
    const units = Number.isFinite(Number(sleeve.units)) ? Math.max(Number(sleeve.units), 0) : 0;
    const executionRatio = Number.isFinite(Number(sleeve.executionRatio)) ? Number(sleeve.executionRatio) : 1;
    const multiplier = units * executionRatio;
    if (multiplier <= 0) return;

    rows.slice(2).forEach((row) => {
      const mondayKey = row?.[0];
      if (!mondayKey) return;
      if (!String(mondayKey).match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/)) return;
      let acc = byMonday.get(mondayKey);
      if (!acc) {
        acc = new Array(rowLen).fill('');
        acc[0] = mondayKey;
        dayEntries.forEach(([, col]) => {
          acc[col] = 0;
        });
        byMonday.set(mondayKey, acc);
      }
      dayEntries.forEach(([dayKey, col]) => {
        if (!allowedDays.has(dayKey)) return;
        const value = parseNumericCell(row[col]);
        acc[col] = (Number(acc[col]) || 0) + ((value || 0) * multiplier);
      });
    });
  });

  const sortedKeys = Array.from(byMonday.keys()).sort((a, b) => {
    const da = parseDateFromRowLabel(a);
    const db = parseDateFromRowLabel(b);
    if (!da || !db) return 0;
    return da - db;
  });

  return [header0, header1, ...sortedKeys.map((key) => byMonday.get(key))];
}

export function buildPortfolioDailySeries(rows, contractMultiplier = 1, contractSize = 1, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  if (!rows || rows.length < 3) return [];
  const headerRow = rows[1] || [];
  const idxMonday = headerRow.findIndex((cell) => String(cell || '').toLowerCase() === 'monday');
  const idxTuesday = headerRow.findIndex((cell) => String(cell || '').toLowerCase() === 'tuesday');
  const idxWednesday = headerRow.findIndex((cell) => String(cell || '').toLowerCase() === 'wednesday');
  const idxThursday = headerRow.findIndex((cell) => String(cell || '').toLowerCase() === 'thursday');
  const idxFriday = headerRow.findIndex((cell) => String(cell || '').toLowerCase() === 'friday');
  const dayCols = [
    idxMonday >= 0 ? idxMonday : 2,
    idxTuesday >= 0 ? idxTuesday : 3,
    idxWednesday >= 0 ? idxWednesday : 4,
    idxThursday >= 0 ? idxThursday : 5,
    idxFriday >= 0 ? idxFriday : 6,
  ];

  const points = [];
  rows.slice(2).forEach((row) => {
    const mondayKey = row[0];
    if (!mondayKey) return;
    dayCols.forEach((col, index) => {
      const date = weekDayDateKeyFromMonday(mondayKey, index);
      if (!date) return;
      const rawValue = parseNumericCell(row[col]) || 0;
      const dailyPnl = rawValue * contractMultiplier * contractSize;
      points.push({ date, dailyPnl });
    });
  });

  points.sort((a, b) => a.date.localeCompare(b.date));

  let cumulative = 0;
  return points.map((point) => {
    const startingEquity = baseValue + cumulative;
    const equityScale = baseValue > 0 ? Math.max(startingEquity, 0) / baseValue : 1;
    const scaledDailyPnl = point.dailyPnl * equityScale;
    cumulative += scaledDailyPnl;
    return {
      date: point.date,
      dailyPnl: Math.round(scaledDailyPnl * 100) / 100,
      cumulative: Math.round(cumulative * 100) / 100,
      normalized: Math.round((baseValue + cumulative) * 100) / 100,
      dailyReturnPct: startingEquity > 0
        ? Math.round(((scaledDailyPnl / startingEquity) * 100) * 10000) / 10000
        : 0,
    };
  });
}

/**
 * Build a portfolio daily series (same shape as buildPortfolioDailySeries) from
 * an equity-curve "returns sleeve": an ordered array of { date, ret } daily
 * fractional returns. Only `allocationPct` (0–100) of the capital base rides the
 * strategy; the rest stays flat. The allocated slice compounds the daily
 * returns; the resulting equity = baseValue + (allocated growth).
 *
 * @param {Array<{date: string, ret: number}>} returns
 * @param {number} allocationPct  0–100, share of capital base allocated
 * @param {number} baseValue      total capital base
 */
export function buildEquityStrategyDailySeries(returns, allocationPct = 100, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  if (!Array.isArray(returns) || returns.length === 0) return [];
  const alloc = Math.min(Math.max(Number(allocationPct) || 0, 0), 100) / 100;
  const allocatedStart = baseValue * alloc;
  const uninvested = baseValue - allocatedStart;

  const sorted = [...returns]
    .filter((p) => p && typeof p.date === 'string' && Number.isFinite(Number(p.ret)))
    .sort((a, b) => a.date.localeCompare(b.date));

  let allocated = allocatedStart;
  let prevEquity = baseValue;
  return sorted.map((point) => {
    const startingEquity = prevEquity;
    const growth = allocated * Number(point.ret); // dollar P&L for the day
    allocated += growth;
    const equity = uninvested + allocated;
    prevEquity = equity;
    return {
      date: point.date,
      dailyPnl: Math.round(growth * 100) / 100,
      cumulative: Math.round((equity - baseValue) * 100) / 100,
      normalized: Math.round(equity * 100) / 100,
      dailyReturnPct: startingEquity > 0
        ? Math.round(((growth / startingEquity) * 100) * 10000) / 10000
        : 0,
    };
  });
}

/**
 * Build an Equity Portfolio scenario series. `weighting` picks how each leg's share
 * is set: 'equal' = allocation% × leverage (Fyntrak "EFF. WEIGHT", 20% at 1× / 45%
 * at 2.25×); 'position' = equity/trade ÷ total × leverage (AAPL ≈ 71%, the $10k legs
 * ≈ 7% each, capping the high-fliers).
 * With `compound` (chart + Performance/Risk stats) profits reinvest —
 *   equity(t) = equity(t-1) × (1 + Σ weightᵢ × leverage × tradeReturnᵢ(t)) —
 * so the curve grows on the running balance and leverage compounds (2.25× grows
 * faster than 2.25× the 1×). Without it (default — calendar + monthly returns) the
 * blend is fixed-notional (Σ per-trade ROI on the base), keeping each month's % on
 * the base so the Monthly Returns table stays comparable.
 *
 * Each leg's cumulative curve is rebuilt from its daily returns within range
 * (anchored at 0% on the first in-range date) and forward-filled across dates it
 * doesn't trade, so a leg holds its last cumulative value on missing days and
 * contributes 0% before it starts. Output matches buildPortfolioDailySeries /
 * buildEquityStrategyDailySeries: [{date, dailyPnl, cumulative, normalized, dailyReturnPct}].
 *
 * @param {Array<{strategyName: string|null, weightPct: number, found: boolean}>} legs resolved legs
 * @param {number} leverage portfolio-wide multiplier (1.0, 2.25, …)
 * @param {Map<string, {returns: Array<{date:string, ret:number}>}>} strategyByName name → strategy
 * @param {{startDate?: string|null, endDate?: string|null}} [range] inclusive ISO date bounds
 * @param {number} [baseValue] capital base the % curve scales onto
 * @param {boolean} [compound] reinvest profits (chart/stats) vs fixed-notional Σ
 * @param {'equal'|'position'} [weighting] how each leg's blend share is set
 */
export function buildEquityPortfolioSeries(
  legs,
  leverage,
  strategyByName,
  range = {},
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
  compound = false,
  weighting = 'equal',
) {
  const lev = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  const startDate = range.startDate || null;
  const endDate = range.endDate || null;

  // Per leg: recover each trade's scale-invariant % return from the FIXED-NOTIONAL
  // stored curve (equity = base × (1 + Σ per-trade ROI)), then COMPOUND the weighted
  // blend at the portfolio level — equityₜ = equityₜ₋₁ × (1 + Σ wᵢ·retᵢ). This
  // reinvests profits (so leverage compounds period-over-period) the way Fyntrak's
  // compounded curve does, instead of a static weighted sum of cumulative returns.
  //
  // factorₜ₋₁ × retₜ telescopes back to the trade's original % on the leg's base,
  // independent of the window, so a windowed view simply compounds the in-window
  // trades starting fresh at baseValue.
  // Two weightings, both scaled by leverage:
  //   'equal'    — weight = allocation% × leverage (Fyntrak "EFF. WEIGHT": 20% at
  //                1×, 45% at 2.25×); all five algos pull equally.
  //   'position' — weight = equity/trade ÷ Σ equity/trade × leverage, so AAPL
  //                ($100k) ≈ 71% and the four $10k legs ≈ 7% each (caps the
  //                high-fliers). Admin allocation/enabled overrides flow through
  //                leg.weightPct (a disabled / 0% leg drops out either way).
  const usable = (Array.isArray(legs) ? legs : []).filter(
    (l) => l && l.found && l.strategyName && (Number(l.weightPct) || 0) > 0,
  );
  const totalEqPerTrade = weighting === 'position'
    ? usable.reduce((s, l) => s + (Number(l.equityPerTrade) || 0), 0)
    : 0;
  const legRets = [];
  usable.forEach((leg) => {
    const strat = strategyByName?.get ? strategyByName.get(leg.strategyName) : null;
    const returns = Array.isArray(strat?.returns) ? strat.returns : [];
    const weight = weighting === 'position'
      ? (totalEqPerTrade > 0 ? lev * (Number(leg.equityPerTrade) / totalEqPerTrade) : 0)
      : lev * (Number(leg.weightPct) / 100);
    if (weight === 0) return;

    const ordered = returns
      .filter((p) => p && typeof p.date === 'string' && Number.isFinite(Number(p.ret)))
      .sort((a, b) => a.date.localeCompare(b.date));

    let factor = 1; // equity_t / leg base (telescoped product of all daily returns)
    const retByDate = new Map();
    ordered.forEach((p) => {
      if (endDate && p.date > endDate) return;
      const r = Number(p.ret);
      if (startDate && p.date < startDate) { factor *= (1 + r); return; }
      const pnlPct = factor * r * 100; // the trade's original % return on the leg base
      retByDate.set(p.date, (retByDate.get(p.date) || 0) + pnlPct);
      factor *= (1 + r);
    });
    if (retByDate.size > 0) legRets.push({ weight, retByDate });
  });

  if (legRets.length === 0) return [];

  const allDates = Array.from(
    new Set(legRets.flatMap((c) => Array.from(c.retByDate.keys()))),
  ).sort((a, b) => a.localeCompare(b));

  // Blend the weighted per-trade returns. `compound` reinvests profits (chart +
  // stats); the default fixed-notional running Σ keeps each value on the base so
  // monthly returns match Fyntrak. Weights include leverage either way.
  let normalized = baseValue;
  let cumPct = 0;
  let prevNormalized = baseValue;
  return allDates.map((date) => {
    let portfolioPct = 0; // blended % move on this date
    legRets.forEach((curve) => {
      if (curve.retByDate.has(date)) portfolioPct += curve.weight * curve.retByDate.get(date);
    });
    if (compound) {
      normalized *= (1 + portfolioPct / 100);
    } else {
      cumPct += portfolioPct;
      normalized = baseValue * (1 + cumPct / 100);
    }
    const cumulative = normalized - baseValue;
    const dailyPnl = normalized - prevNormalized;
    const dailyReturnPct = prevNormalized > 0 ? (dailyPnl / prevNormalized) * 100 : 0;
    prevNormalized = normalized;
    return {
      date,
      dailyPnl: Math.round(dailyPnl * 100) / 100,
      cumulative: Math.round(cumulative * 100) / 100,
      normalized: Math.round(normalized * 100) / 100,
      dailyReturnPct: Math.round(dailyReturnPct * 10000) / 10000,
    };
  });
}

/**
 * OG Equity Portfolio: keep the frozen Fyntrak 1× history through the last export
 * date, then stitch live OG-leg webhook blend for dates after that anchor.
 */
export function buildOgEquityPortfolioBlendedSeries(
  fyntrakPoints,
  legs,
  strategyByName,
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
  compound = false,
  weighting = 'equal',
) {
  const sortedFyntrak = [...(Array.isArray(fyntrakPoints) ? fyntrakPoints : [])]
    .filter((p) => p && typeof p.date === 'string' && Number.isFinite(Number(p.equity)))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (sortedFyntrak.length < 2) return [];

  const lastFyntrakDate = sortedFyntrak[sortedFyntrak.length - 1].date;
  const baselineSeries = buildEquityCurveWindowSeries(sortedFyntrak, {}, baseValue, true);
  if (!baselineSeries.length) return [];

  const usable = (Array.isArray(legs) ? legs : []).filter(
    (l) => l && l.found && l.strategyName && (Number(l.weightPct) || 0) > 0,
  );
  if (!usable.length) return baselineSeries;

  const liveFull = buildEquityPortfolioSeries(
    usable,
    1,
    strategyByName,
    {},
    baseValue,
    compound,
    weighting,
  );
  const liveExtension = liveFull.filter((p) => p.date > lastFyntrakDate);
  if (!liveExtension.length) return baselineSeries;

  const baselineEndNorm = baselineSeries[baselineSeries.length - 1]?.normalized ?? baseValue;
  const shift = baselineEndNorm - liveExtension[0].normalized;

  let prevNorm = baselineEndNorm;
  const stitched = liveExtension.map((p) => {
    const normalized = Math.round((p.normalized + shift) * 100) / 100;
    const dailyPnl = Math.round((normalized - prevNorm) * 100) / 100;
    const dailyReturnPct = prevNorm > 0
      ? Math.round(((dailyPnl / prevNorm) * 100) * 10000) / 10000
      : 0;
    const cumulative = Math.round((normalized - baseValue) * 100) / 100;
    prevNorm = normalized;
    return {
      date: p.date,
      normalized,
      dailyPnl,
      cumulative,
      dailyReturnPct,
    };
  });

  const baselineDates = new Set(baselineSeries.map((p) => p.date));
  return [
    ...baselineSeries,
    ...stitched.filter((p) => !baselineDates.has(p.date)),
  ].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Fyntrak-style "linear" leverage.
 *
 * Real (compounded) leverage multiplies each day's blended return by L and then
 * compounds it, so a 2.25× curve grows MUCH faster than 2.25× the 1× annualized
 * (leverage compounds on itself — ~2.7× the 1× CAGR over a multi-year span).
 * Fyntrak instead reports its 2.25× as ≈ L × the 1× annualized — a softened,
 * linear scaling, not a true leveraged track record.
 *
 * This reproduces that: take the UNLEVERED (1×) curve and re-scale it so its
 * annualized return becomes L × a, preserving the curve's shape/drawdowns —
 *   value'(t) = base × (value₁ₓ(t) / base) ^ s,   s = ln(1 + L·a) / ln(1 + a)
 * where `a` is the 1× CAGR over the FULL span (passed via `fullSeries` so the
 * exponent is stable across YTD/1Y/All windows). Daily $ / % fields are
 * recomputed off the scaled curve so the chart, metrics, and calendar all agree.
 *
 * Sign of each day is preserved (a winning day stays a win), so win rate is
 * unchanged and Sharpe stays ~invariant — exactly how leverage should behave.
 */
export function applyLinearLeverageToSeries(
  series,
  leverage,
  fullSeries = null,
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
) {
  const rows = Array.isArray(series) ? series : [];
  const L = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  if (rows.length < 2 || L === 1) return rows;

  const ref = (Array.isArray(fullSeries) && fullSeries.length >= 2) ? fullSeries : rows;
  const startVal = Number(ref[0]?.normalized) || baseValue;
  const endVal = Number(ref[ref.length - 1]?.normalized) || baseValue;
  const startMs = parseSeriesDateKey(ref[0]?.date);
  const endMs = parseSeriesDateKey(ref[ref.length - 1]?.date);
  const years = (startMs && endMs)
    ? Math.max((endMs - startMs) / (365.25 * 24 * 60 * 60 * 1000), 0)
    : 0;
  if (!(startVal > 0) || !(endVal > 0) || !(years > 0)) return rows;

  const a = Math.pow(endVal / startVal, 1 / years) - 1; // 1× annualized (CAGR)
  const targetA = L * a; // Fyntrak: annualized × leverage
  const lnBase = Math.log(1 + a);
  if (!(Math.abs(lnBase) > 1e-12) || 1 + targetA <= 0) return rows; // flat / degenerate → leave 1×
  const s = Math.log(1 + targetA) / lnBase;

  let prev = baseValue;
  return rows.map((row, i) => {
    const ratio = (Number(row?.normalized) || baseValue) / baseValue;
    const scaled = ratio > 0 ? baseValue * Math.pow(ratio, s) : baseValue;
    const normalized = Math.round(scaled * 100) / 100;
    const dailyPnl = i === 0 ? 0 : normalized - prev;
    const dailyReturnPct = (i > 0 && prev > 0) ? (dailyPnl / prev) * 100 : 0;
    prev = normalized;
    return {
      ...row,
      normalized,
      cumulative: Math.round((normalized - baseValue) * 100) / 100,
      dailyPnl: Math.round(dailyPnl * 100) / 100,
      dailyReturnPct: Math.round(dailyReturnPct * 10000) / 10000,
    };
  });
}

/**
 * Calendar-day stats (Fyntrak shape) derived straight from a normalized equity
 * series. Used when a scenario's curve is built by RE-SCALING (Fyntrak-linear
 * leverage) rather than an independent per-trade calc — so the calendar's daily
 * $ / % come from the same curve as the chart and metrics and agree to the cent.
 */
export function buildDailyStatsFromSeries(series, positions = 1) {
  const rows = Array.isArray(series) ? series : [];
  const out = new Map();
  const vol = Math.max(1, Number(positions) || 1);
  for (let i = 1; i < rows.length; i += 1) {
    const prev = Number(rows[i - 1]?.normalized);
    const cur = Number(rows[i]?.normalized);
    const date = rows[i]?.date;
    if (!date || !Number.isFinite(prev) || !Number.isFinite(cur)) continue;
    const pnl = cur - prev;
    if (!pnl) continue;
    out.set(date, {
      pnl: Math.round(pnl * 100) / 100,
      roi: prev > 0 ? ((cur / prev) - 1) * 100 : 0,
      volume: vol,
      trades: 1,
      wins: pnl > 0 ? 1 : 0,
      losses: pnl < 0 ? 1 : 0,
    });
  }
  return out;
}

/**
 * Real (compounded) leverage applied to a stored equity curve: lever each day's
 * return by L and compound it into the running balance — value(t) = value(t−1) ×
 * (1 + L · r(t)). Used for the OG (Fyntrak-ingested) 2.25× book when the admin
 * picks "real compounded leverage" instead of the Fyntrak-linear scaling. The
 * input series must already be base-rebased (normalized starts at baseValue).
 */
export function applyCompoundLeverageToSeries(series, leverage, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  const rows = Array.isArray(series) ? series : [];
  const L = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  if (rows.length < 2 || L === 1) return rows;
  let prev = baseValue;
  return rows.map((row, i) => {
    if (i === 0) {
      prev = baseValue;
      return { ...row, normalized: Math.round(baseValue * 100) / 100, dailyPnl: 0, cumulative: 0, dailyReturnPct: 0 };
    }
    const r = (Number(row?.dailyReturnPct) || 0) / 100;
    const normalized = prev * (1 + L * r);
    const dailyPnl = normalized - prev;
    const dailyReturnPct = prev > 0 ? (dailyPnl / prev) * 100 : 0;
    prev = normalized;
    return {
      ...row,
      normalized: Math.round(normalized * 100) / 100,
      cumulative: Math.round((normalized - baseValue) * 100) / 100,
      dailyPnl: Math.round(dailyPnl * 100) / 100,
      dailyReturnPct: Math.round(dailyReturnPct * 10000) / 10000,
    };
  });
}

/** Fixed-notional equity curve from trade-journal rows (matches server + calendar). */
export function buildEquityPointsFromTrades(trades, baseCapital = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  const ordered = [...(trades || [])].sort((a, b) => {
    if (a.trade_date !== b.trade_date) return String(a.trade_date).localeCompare(String(b.trade_date));
    return (a.sort_index || 0) - (b.sort_index || 0);
  });
  if (!ordered.length) return [];

  const byDate = new Map();
  const allHavePnl = ordered.every((t) => Number.isFinite(Number(t.net_pnl)));
  const allHaveRoi = ordered.every((t) => Number.isFinite(Number(t.net_roi)));
  // Dollars win only when roi is just a display-rounding of pnl (2dp roi =
  // half-step $5 on $100k). Stock 1H algos store compounded dollars in pnl
  // with fixed-notional roi — roi stays authoritative there. (Matches server.)
  const roiHalfStep = baseCapital * 0.00005 + 0.01;
  const pnlMatchesRoi = allHavePnl && allHaveRoi && ordered.every((t) => {
    const pnl = Number(t.net_pnl) || 0;
    const roiDollars = baseCapital * ((Number(t.net_roi) || 0) / 100);
    return Math.abs(pnl - roiDollars) <= roiHalfStep;
  });

  if (allHaveRoi && !pnlMatchesRoi) {
    let runningPct = 0;
    ordered.forEach((t) => {
      runningPct += Number(t.net_roi) || 0;
      byDate.set(t.trade_date, baseCapital * (1 + runningPct / 100));
    });
  } else {
    // Exact dollars — summing display-rounded roi drifts the curve
    // (e.g. a $915 trade charted as $920).
    let running = 0;
    ordered.forEach((t) => {
      running += Number(t.net_pnl) || 0;
      byDate.set(t.trade_date, baseCapital + running);
    });
  }

  const seed = new Date(`${ordered[0].trade_date}T00:00:00Z`);
  seed.setUTCDate(seed.getUTCDate() - 1);
  const seedDate = seed.toISOString().slice(0, 10);
  if (!byDate.has(seedDate)) byDate.set(seedDate, baseCapital);

  return Array.from(byDate.entries())
    .map(([date, equity]) => ({
      date,
      equity: Math.round(equity * 100) / 100,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Aggregate trade journal rows by day (Fyntrak CalendarDayStats shape). */
export function buildDailyStatsFromTrades(trades) {
  const out = new Map();
  (trades || []).forEach((t) => {
    const date = String(t.trade_date || '').slice(0, 10);
    if (!date) return;
    if (!out.has(date)) {
      out.set(date, {
        pnl: 0,
        roi: 0,
        volume: 0,
        trades: 0,
        wins: 0,
        losses: 0,
      });
    }
    const stats = out.get(date);
    const pnl = Number(t.net_pnl) || 0;
    stats.pnl += pnl;
    stats.roi += Number(t.net_roi) || 0;
    stats.volume += Number(t.volume) || 0;
    stats.trades += 1;
    if (String(t.status || '').toLowerCase() === 'win') stats.wins += 1;
    else if (String(t.status || '').toLowerCase() === 'loss') stats.losses += 1;
    else if (pnl > 0) stats.wins += 1;
    else if (pnl < 0) stats.losses += 1;
  });
  return out;
}

/**
 * Equity Portfolio calendar — aggregate each leg's trade journal by day, then
 * blend weighted ROI and sum Vol/Trades/Win-Loss (mirrors Fyntrak dataService).
 */
export function buildEquityPortfolioCalendarStats(
  legs,
  leverage,
  strategyByName,
  baseCapital = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
  weighting = 'equal',
) {
  const lev = Number.isFinite(Number(leverage)) ? Number(leverage) : 1;
  const dateMap = new Map();
  // Match buildEquityPortfolioSeries weighting so the calendar agrees with the chart.
  const eligible = (Array.isArray(legs) ? legs : []).filter(
    (l) => l && l.found && l.enabled !== false && l.strategyName && (Number(l.weightPct) || 0) > 0,
  );
  const totalEqPerTrade = weighting === 'position'
    ? eligible.reduce((s, l) => s + (Number(l.equityPerTrade) || 0), 0)
    : 0;

  eligible.forEach((leg) => {
    const strat = strategyByName?.get ? strategyByName.get(leg.strategyName) : null;
    const trades = strat?.trades;
    if (!Array.isArray(trades) || !trades.length) return;

    const weight = weighting === 'position'
      ? (totalEqPerTrade > 0 ? lev * (Number(leg.equityPerTrade) / totalEqPerTrade) : 0)
      : lev * ((Number(leg.weightPct) || 0) / 100);
    if (weight === 0) return;

    const legDays = buildDailyStatsFromTrades(trades);
    legDays.forEach((day, date) => {
      if (!dateMap.has(date)) {
        dateMap.set(date, {
          pnl: 0,
          roi: 0,
          volume: 0,
          trades: 0,
          wins: 0,
          losses: 0,
        });
      }
      const agg = dateMap.get(date);
      agg.roi += weight * day.roi;
      agg.volume += day.volume;
      agg.trades += day.trades;
      agg.wins += day.wins;
      agg.losses += day.losses;
    });
  });

  dateMap.forEach((stats, date) => {
    stats.pnl = Math.round((stats.roi / 100) * baseCapital * 100) / 100;
    dateMap.set(date, stats);
  });

  return dateMap;
}

// RiskLo's PLTR backtest CSV compounds ~10× hotter than the PLTR source Fyntrak
// displays, so even after equityPerTrade scaling its trade size + P&L still read
// 10× large. Shift PLTR one more decimal so the List of Trades matches Fyntrak.
// DISPLAY-ONLY (net_roi % is untouched, so the curve/metrics are unaffected).
const LEG_TRADE_DISPLAY_SCALE = { PLTR: 0.1 };

/** Flatten per-leg trade journals for Equity Portfolio List of Trades (Fyntrak-style). */
export function collectEquityPortfolioLegTrades(legs, strategyByName, instrumentMap = null) {
  const out = [];
  (Array.isArray(legs) ? legs : []).forEach((leg) => {
    if (!leg?.found || leg.enabled === false || !leg.strategyName) return;
    const strat = strategyByName?.get ? strategyByName.get(leg.strategyName) : null;
    const trades = strat?.trades;
    if (!Array.isArray(trades) || !trades.length) return;
    const configuredSymbol = instrumentMap
      ? resolveConfiguredDisplaySymbol(leg.strategyName, instrumentMap, '')
      : '';
    const displaySymbol = configuredSymbol || null;
    // The ingested CSV trades are all sized on a $100k notional, but each
    // portfolio leg actually trades its own equityPerTrade (AAPL $100k, the rest
    // $10k). Rescale the displayed volume + net P&L to that capital so the list
    // matches Fyntrak's per-leg sizing. net_roi is the scale-invariant % return,
    // so it is left untouched and the curve/stats are unaffected.
    const scale = ((Number(leg.equityPerTrade) || DEFAULT_VECTOR_CAPITAL_BASE_VALUE)
      / DEFAULT_VECTOR_CAPITAL_BASE_VALUE) * (LEG_TRADE_DISPLAY_SCALE[leg.ticker] || 1);
    const r2 = (n) => (n == null || !Number.isFinite(Number(n))
      ? n
      : Math.round(Number(n) * scale * 100) / 100);
    trades.forEach((t) => {
      out.push({
        trade_date: t.trade_date,
        strategyName: leg.strategyName,
        symbol: displaySymbol || t.symbol || leg.ticker || '',
        status: t.status,
        volume: r2(t.volume),
        entry_price: t.entry_price,
        exit_price: t.exit_price,
        net_pnl: r2(t.net_pnl),
        net_roi: t.net_roi,
        // Long/Short must survive the rescale — dropping it here blanked the
        // Side column for every Equity Portfolio leg even when the journal
        // rows carried a direction.
        position: t.position || t.market_position || null,
      });
    });
  });
  return out;
}

/**
 * Windowed series from a stored equity curve (points: [{date, equity}]).
 *
 * Default (FIXED-NOTIONAL, e.g. Master Multi Account / Vector strategy curves):
 * re-base to `baseValue` and add only the window's P&L (Σ in-window PnL ÷ original
 * base) — a 1Y / YTD return is the window's gain on the fixed $100k notional.
 *
 * With `compound` (a REAL compounding equity curve like Kairos, where the curve is
 * an actual growing balance): re-base by RATIO — normalized = baseValue × (equity ÷
 * window-start equity) — so a 1Y / YTD return is (end ÷ start − 1), the true % move
 * over the window. Without this, dividing the window's $ gain by the original $100k
 * over-states the return by the curve's growth factor (e.g. a +$109k YTD gain on a
 * $746k balance reads as +109% instead of +14.6%).
 */
export function buildEquityCurveWindowSeries(points, range = {}, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE, compound = false) {
  const rows = (Array.isArray(points) ? points : [])
    .filter((p) => p && typeof p.date === 'string' && Number.isFinite(Number(p.equity)))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (rows.length < 2) return [];
  const originalBase = Number(rows[0].equity) || baseValue;
  const startDate = range.startDate || null;
  const endDate = range.endDate || null;

  // Equity at the last point strictly before the window (or the curve's base).
  let windowStartEquity = Number(rows[0].equity);
  let anchor = null;
  if (startDate) {
    for (let i = 0; i < rows.length; i += 1) {
      if (rows[i].date < startDate) { windowStartEquity = Number(rows[i].equity); anchor = rows[i]; } else break;
    }
  }

  const r2 = (n) => Math.round(n * 100) / 100;
  const r4 = (n) => Math.round(n * 10000) / 10000;
  const out = [];
  let prevNormalized = baseValue;
  // Seed the curve at exactly baseValue on the pre-window date (Fyntrak pushes
  // value=0 there) so the chart starts clean at the capital base. When a window
  // start is set, omit the pre-window anchor so calendar-year breakdowns and
  // admin history cutoffs do not show phantom years before the floor.
  if (anchor && !startDate) {
    out.push({ date: anchor.date, normalized: r2(baseValue), dailyPnl: 0, cumulative: 0, dailyReturnPct: 0 });
  }

  rows.forEach((p) => {
    if (startDate && p.date < startDate) return;
    if (endDate && p.date > endDate) return;
    const eq = Number(p.equity);
    // compound → rebase by ratio (true % move from window start); else fixed-notional
    // (window's $ P&L on the original $100k base).
    const normalized = compound
      ? (windowStartEquity > 0 ? baseValue * (eq / windowStartEquity) : baseValue)
      : baseValue * (1 + (eq - windowStartEquity) / originalBase);
    const dailyPnl = normalized - prevNormalized;
    const dailyReturnPct = prevNormalized > 0 ? (dailyPnl / prevNormalized) * 100 : 0;
    prevNormalized = normalized;
    out.push({
      date: p.date,
      normalized: r2(normalized),
      dailyPnl: r2(dailyPnl),
      cumulative: r2(normalized - baseValue),
      dailyReturnPct: r4(dailyReturnPct),
    });
  });
  return out;
}

export function summarizePerformance(series, baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE) {
  if (!series || series.length === 0) {
    return {
      startDate: null,
      endDate: null,
      totalReturnPct: 0,
      annualizedReturnPct: 0,
      maxDrawdownPct: 0,
      winRatePct: 0,
      profitFactor: 0,
      avgDailyReturnPct: 0,
      endingValue: baseValue,
      positiveDays: 0,
      totalDays: 0,
      yearsElapsed: 0,
    };
  }

  const startDate = parseSeriesDateKey(series[0].date);
  const endDate = parseSeriesDateKey(series[series.length - 1].date);
  const years = Math.max((endDate - startDate) / (365.25 * 24 * 60 * 60 * 1000), 0);
  const endingValue = series[series.length - 1].normalized;
  const totalReturnPct = ((endingValue / baseValue) - 1) * 100;
  const annualizedReturnPct = years > 0 ? (Math.pow(endingValue / baseValue, 1 / years) - 1) * 100 : totalReturnPct;

  let peak = baseValue;
  let maxDrawdownPct = 0;
  let positiveDays = 0;
  let nonZeroDays = 0;
  let grossProfit = 0;
  let grossLoss = 0;
  let dailyReturnPctSum = 0;

  series.forEach((point) => {
    peak = Math.max(peak, point.normalized);
    if (peak > 0) {
      const drawdownPct = ((point.normalized - peak) / peak) * 100;
      maxDrawdownPct = Math.min(maxDrawdownPct, drawdownPct);
    }

    if (point.dailyPnl !== 0) {
      nonZeroDays += 1;
      if (point.dailyPnl > 0) {
        positiveDays += 1;
        grossProfit += point.dailyPnl;
      } else {
        grossLoss += Math.abs(point.dailyPnl);
      }
    }

    dailyReturnPctSum += point.dailyReturnPct || 0;
  });

  return {
    startDate: series[0].date,
    endDate: series[series.length - 1].date,
    totalReturnPct: Math.round(totalReturnPct * 100) / 100,
    annualizedReturnPct: Math.round(annualizedReturnPct * 100) / 100,
    maxDrawdownPct: Math.round(Math.abs(maxDrawdownPct) * 100) / 100,
    winRatePct: nonZeroDays > 0 ? Math.round(((positiveDays / nonZeroDays) * 100) * 100) / 100 : 0,
    profitFactor: grossLoss > 0 ? Math.round((grossProfit / grossLoss) * 100) / 100 : (grossProfit > 0 ? Infinity : 0),
    avgDailyReturnPct: nonZeroDays > 0 ? Math.round((dailyReturnPctSum / nonZeroDays) * 100) / 100 : 0,
    endingValue: Math.round(endingValue * 100) / 100,
    positiveDays,
    totalDays: nonZeroDays,
    yearsElapsed: years,
  };
}

/**
 * Trailing period returns from daily normalized value series (same units as chart: account value).
 * Windows: last ~7 calendar days, ~30 days, ~365 days from last observation.
 */
export function computePeriodReturns(series) {
  if (!series?.length) {
    return {
      weekPct: null,
      monthPct: null,
      yearPct: null,
      weekDollar: null,
      monthDollar: null,
      yearDollar: null,
    };
  }

  const end = series[series.length - 1];
  const endTime = parseSeriesDateKey(end.date);

  const cutoffIso = (calendarDays) => {
    const d = new Date(endTime);
    d.setUTCDate(d.getUTCDate() - calendarDays);
    return d.toISOString().slice(0, 10);
  };

  const firstOnOrAfter = (cutoffStr) => {
    for (let i = 0; i < series.length; i += 1) {
      if (series[i].date >= cutoffStr) return series[i];
    }
    return series[0];
  };

  const wStart = firstOnOrAfter(cutoffIso(7));
  const mStart = firstOnOrAfter(cutoffIso(30));
  const yStart = firstOnOrAfter(cutoffIso(365));

  const pct = (a, b) => {
    if (!a || !b || a.normalized <= 0 || b.normalized <= 0) return null;
    return Math.round((((b.normalized / a.normalized) - 1) * 100) * 100) / 100;
  };

  const dollars = (a, b) => {
    if (!a || !b) return null;
    return Math.round((b.normalized - a.normalized) * 100) / 100;
  };

  return {
    weekPct: pct(wStart, end),
    monthPct: pct(mStart, end),
    yearPct: pct(yStart, end),
    weekDollar: dollars(wStart, end),
    monthDollar: dollars(mStart, end),
    yearDollar: dollars(yStart, end),
  };
}

/** Largest peak-to-trough drop in account dollars (negative). */
export function computeMaxDrawdownDollars(series) {
  if (!series?.length) return null;
  let peak = series[0].normalized;
  let maxDd = 0;
  series.forEach((p) => {
    peak = Math.max(peak, p.normalized);
    const dd = p.normalized - peak;
    maxDd = Math.min(maxDd, dd);
  });
  return Math.round(maxDd * 100) / 100;
}

/** Average annual dollar gain implied by CAGR on initial base (for money-mode annualized row). */
export function annualizedDollarFromCagr(baseValue, endingValue, years) {
  if (!years || years <= 0 || !baseValue || baseValue <= 0 || !endingValue) return null;
  const cagr = Math.pow(endingValue / baseValue, 1 / years) - 1;
  return Math.round(baseValue * cagr * 100) / 100;
}

/** e.g. "2026-03" -> "Mar 2026" */
export function formatMonthKeyLabel(monthKey) {
  const parts = String(monthKey).split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!y || !m) return monthKey;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Calendar-month P&L and % return from a daily normalized equity series.
 *
 * When `fixedBaseCapital` is set (all Vector strategies, including Kairos),
 * monthly % = (month P&L ÷ fixed base) × 100 — always divides by the notional base
 * (e.g. $100k), not the growing month-start equity.
 *
 * Without `fixedBaseCapital` (benchmarks), monthly % is linked month-over-month:
 * (month-end ÷ prior month-end − 1) × 100, matching Fyntrak benchmark close-to-close.
 */
export function buildMonthlyRows(series, options = {}) {
  const { fixedBaseCapital = null } = options;
  const rows = Array.isArray(series) ? series : [];
  const months = [];
  let prevEnd = null;
  let cur = null;
  rows.forEach((p) => {
    const v = Number(p?.normalized);
    if (!p?.date || !Number.isFinite(v)) return;
    const key = p.date.slice(0, 7);
    if (!cur || cur.key !== key) {
      if (cur) months.push(cur);
      cur = { key, base: prevEnd ?? v, end: v };
    } else {
      cur.end = v;
    }
    prevEnd = v;
  });
  if (cur) months.push(cur);
  return months.map((m) => {
    const pnl = m.end - m.base;
    let pct = null;
    if (Number.isFinite(fixedBaseCapital) && fixedBaseCapital > 0) {
      pct = (pnl / fixedBaseCapital) * 100;
    } else if (m.base > 0) {
      pct = ((m.end / m.base) - 1) * 100;
    }
    return {
      key: m.key,
      pnl: Math.round(pnl * 100) / 100,
      pct: pct !== null ? Math.round(pct * 100) / 100 : null,
    };
  });
}

/**
 * Per-calendar-month portfolio vs benchmark breakdown (Monthly Returns table + KPI strip).
 * Portfolio uses Fyntrak fixed-notional % when `fixedBaseCapital` is provided.
 */
export function computeMonthlyReturnBreakdown(portfolioSeries, benchmarkSeries, options = {}) {
  const { fixedBaseCapital = null } = options;
  const pMap = new Map(
    buildMonthlyRows(portfolioSeries, { fixedBaseCapital }).map((r) => [r.key, r]),
  );
  const bMap = new Map(buildMonthlyRows(benchmarkSeries).map((r) => [r.key, r]));
  const months = new Set([...pMap.keys(), ...bMap.keys()]);
  const sorted = Array.from(months).sort((a, b) => a.localeCompare(b));

  return sorted.map((monthKey) => {
    const p = pMap.get(monthKey);
    const b = bMap.get(monthKey);
    const portPct = p?.pct ?? null;
    const benchPct = b?.pct ?? null;
    const portDollar = p?.pnl ?? null;
    const benchDollar = b?.pnl ?? null;

    let diffPct = null;
    let diffDollar = null;
    if (portPct !== null && benchPct !== null) {
      diffPct = Math.round((portPct - benchPct) * 100) / 100;
    }
    if (portDollar !== null && benchDollar !== null) {
      diffDollar = Math.round((portDollar - benchDollar) * 100) / 100;
    }

    return {
      monthKey,
      label: formatMonthKeyLabel(monthKey),
      portPct,
      benchPct,
      portDollar,
      benchDollar,
      diffPct,
      diffDollar,
    };
  });
}

function groupFirstLastByYear(series) {
  const byYear = new Map();
  if (!series?.length) return new Map();
  series.forEach((p) => {
    if (!p?.date) return;
    const y = String(p.date).slice(0, 4);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push(p);
  });
  const map = new Map();
  byYear.forEach((pts, yearKey) => {
    pts.sort((a, b) => a.date.localeCompare(b.date));
    map.set(yearKey, { first: pts[0], last: pts[pts.length - 1] });
  });
  return map;
}

/**
 * Per-calendar-year return % for one normalized daily series (first-to-last trading day in each year).
 */
export function computeYearlyReturnsFromSeries(series) {
  const map = groupFirstLastByYear(series);
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yearKey, { first, last }]) => {
      let pct = null;
      if (first?.normalized > 0) {
        pct = Math.round((((last.normalized / first.normalized) - 1) * 100) * 100) / 100;
      }
      return { yearKey, label: yearKey, pct };
    });
}

/**
 * Per-calendar-year returns from daily normalized series (first-to-last trading day in each year).
 */
export function computeYearlyReturnBreakdown(portfolioSeries, benchmarkSeries) {
  const pMap = groupFirstLastByYear(portfolioSeries);
  const bMap = groupFirstLastByYear(benchmarkSeries);
  const years = new Set([...pMap.keys(), ...bMap.keys()]);
  const sorted = Array.from(years).sort((a, b) => a.localeCompare(b));

  return sorted.map((yearKey) => {
    const p = pMap.get(yearKey);
    const b = bMap.get(yearKey);
    let portPct = null;
    let benchPct = null;
    let portDollar = null;
    let benchDollar = null;

    if (p?.first && p?.last && p.first.normalized > 0) {
      portPct = Math.round((((p.last.normalized / p.first.normalized) - 1) * 100) * 100) / 100;
      portDollar = Math.round((p.last.normalized - p.first.normalized) * 100) / 100;
    }
    if (b?.first && b?.last && b.first.normalized > 0) {
      benchPct = Math.round((((b.last.normalized / b.first.normalized) - 1) * 100) * 100) / 100;
      benchDollar = Math.round((b.last.normalized - b.first.normalized) * 100) / 100;
    }

    let diffPct = null;
    let diffDollar = null;
    if (portPct !== null && benchPct !== null) {
      diffPct = Math.round((portPct - benchPct) * 100) / 100;
    }
    if (portDollar !== null && benchDollar !== null) {
      diffDollar = Math.round((portDollar - benchDollar) * 100) / 100;
    }

    return {
      yearKey,
      label: yearKey,
      portPct,
      benchPct,
      portDollar,
      benchDollar,
      diffPct,
      diffDollar,
    };
  });
}

/**
 * @param {Record<string, Array<{ date: string, normalized: number }>>} benchmarkSeriesById
 */
export function mergePortfolioAndMultipleBenchmarks(
  portfolioSeries,
  benchmarkSeriesById,
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
) {
  if (!portfolioSeries || portfolioSeries.length === 0) return [];

  const rangeStart = portfolioSeries[0].date;

  const ids = Object.keys(benchmarkSeriesById || {}).filter((id) => benchmarkSeriesById[id]?.length);
  const sortedIds = [...ids].sort((a, b) => a.localeCompare(b));

  const byDate = new Map();
  portfolioSeries.forEach((point) => {
    byDate.set(point.date, {
      date: point.date,
      portfolioValue: point.normalized,
      portfolioDailyPnl: point.dailyPnl,
      benchmarkById: {},
    });
  });

  // Include benchmark dates through the benchmark's latest point (not capped at the
  // scenario's last date). Scenario values forward-fill on those extra days.
  sortedIds.forEach((id) => {
    const series = (benchmarkSeriesById[id] || []).filter(
      (point) => point && point.date >= rangeStart,
    );
    series.forEach((point) => {
      const existing = byDate.get(point.date) || {
        date: point.date,
        portfolioValue: null,
        portfolioDailyPnl: 0,
        benchmarkById: {},
      };
      existing.benchmarkById[id] = point.normalized;
      byDate.set(point.date, existing);
    });
  });

  const rows = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  const lastPerId = new Map(sortedIds.map((id) => [id, baseValue]));
  let lastPortfolio = baseValue;

  return rows.map((row) => {
    if (typeof row.portfolioValue === 'number') {
      lastPortfolio = row.portfolioValue;
    } else {
      row.portfolioValue = lastPortfolio;
    }

    sortedIds.forEach((id) => {
      const v = row.benchmarkById[id];
      if (typeof v === 'number') {
        lastPerId.set(id, v);
      }
      row[`bm_${id}`] = typeof v === 'number' ? v : lastPerId.get(id);
    });
    delete row.benchmarkById;

    row.benchmarkValue = sortedIds.length ? row[`bm_${sortedIds[0]}`] : null;
    row.label = formatDateLabel(row.date);
    return row;
  });
}

export function mergePortfolioAndBenchmarkSeries(
  portfolioSeries,
  benchmarkSeries,
  baseValue = DEFAULT_VECTOR_CAPITAL_BASE_VALUE,
  benchmarkId = 'SPY',
) {
  if (!benchmarkSeries?.length) {
    return mergePortfolioAndMultipleBenchmarks(portfolioSeries, {}, baseValue);
  }
  return mergePortfolioAndMultipleBenchmarks(portfolioSeries, { [benchmarkId]: benchmarkSeries }, baseValue);
}

export function formatDateLabel(dateKey) {
  if (!dateKey) return '';
  const s = String(dateKey);
  // Intraday keys (YYYY-MM-DDTHH:MM…) — used by the live webhook TEST strategies.
  if (s.includes('T')) {
    const dt = new Date(s.length <= 16 ? `${s}:00Z` : s);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }
  const [year, month, day] = s.split('-').map(Number);
  if (!year || !month || !day) return s;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
